import PropTypes from 'prop-types';
import React from 'react';
import useMarketo from '../../../utilities/hooks/useMarketo';
import LoadingSpinner from '../../Elements/LoadingSpinner/LoadingSpinner';
import './MarketoForm.scss';

const MARKETO_BASE_URL = '//hire.themuse.com';
const MUNCHKIN_ID = '084-RRC-895';

const MarketoForm = ({ formId, isHidden }) => {
    const [hasAdblock, setHasAdblock] = React.useState(false);
    const [invalidForm, setIsInvalidForm] = React.useState(false);

    const [scriptLoaded, loadError] = useMarketo({
        baseUrl: MARKETO_BASE_URL,
        munchkinId: MUNCHKIN_ID,
        formId,
        callback: removeMarketoStyles(setIsInvalidForm),
    });

    // sets a 5 second timeout on mount which checks window for MktoForms2
    // if undefined - we can assume user has an adblocker or ghostery enabled
    // clears timeout if we have other form errors
    React.useEffect(() => {
        const checkMarketoExists = () => {
            if (typeof window === 'undefined' || !window.MktoForms2)
                return setHasAdblock(true);
            if (window.MktoForms2) return setHasAdblock(false);
        };

        const timeout = setTimeout(checkMarketoExists, 5000);

        if (invalidForm) clearTimeout(timeout);

        return () => clearTimeout(timeout);
    }, [invalidForm]);

    if (isHidden) return null;

    if (hasAdblock) return adblockMessage;

    if (loadError || invalidForm) return errorMessage;

    // show loading spinner if loading... otherwise show form
    return (
        <div className="component-Form-MarketoForm">
            {!scriptLoaded ? (
                <LoadingSpinner />
            ) : (
                <form id={`mktoForm_${formId}`} className="marketo-form" />
            )}
        </div>
    );
};

const errorMessage = (
    <p className="component-Form-MarketoForm__error">
        We&apos;re having trouble loading the form.
        <br />
        Please refresh the page or try again later.
    </p>
);

const adblockMessage = (
    <p className="component-Form-MarketoForm__error">
        We&apos;re having trouble loading the form.
        <br />
        If you are using an adBlocker, please pause it and refresh the page to
        try again.
    </p>
);

MarketoForm.propTypes = {
    formId: PropTypes.number.isRequired,
    isHidden: PropTypes.bool,
};

MarketoForm.defaultProps = {
    isHidden: false,
};

const removeMarketoStyles = (setHasError) => (mktoForm) => {
    if (!mktoForm) return setHasError(true); // will set error if we have an invalid form id
    const formEl = mktoForm.getFormElem()[0];

    // in-form style tags + filler/styling divs (clear, offset, gutter)
    const uselessTags = Array.from(
        formEl.querySelectorAll('.mktoClear, .mktoOffset, .mktoGutter, style')
    );

    // base + theme styles that marketo loads with the form
    const stylesToRemove = Array.from(
        document.querySelectorAll('#mktoForms2BaseStyle, #mktoForms2ThemeStyle')
    );

    // remove them
    [...stylesToRemove, ...uselessTags].forEach((tag) => tag.remove());

    // remove annoying style tag that marketo attaches to head to style button row
    const styleTags = Array.from(document.head.getElementsByTagName('style'));
    styleTags.forEach((styleTag) => {
        const badStyleTagIdentifier = '/* Add your custom CSS below */';
        const isBadStyleNode = styleTag.textContent.includes(
            badStyleTagIdentifier
        );
       
        if (isBadStyleNode) styleTag.remove();
    });

    // capitalize button first word - lowercase rest
    const buttonElements = Array.from(formEl.querySelectorAll('.mktoButton'));
    buttonElements.forEach((btn) => {
        const btnText =
            btn.textContent[0] + btn.textContent.slice(1).toLowerCase();
        btn.textContent = btnText;
    });

    // remove inline styles from form elements
    const styledEls = Array.from(formEl.querySelectorAll('[style]')).concat(
        formEl
    );
    styledEls.forEach((el) => el.removeAttribute('style'));

    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach((ss) => {
        // disable stylesheets related to marketo form elements
        const formContainsNode = formEl.contains(ss.ownerNode);
        if (formContainsNode) ss.disabled = true;
    });

    formEl.setAttribute('data-styles-ready', 'true');
};

export default React.memo(MarketoForm);
